// src/app/pages/dashboard/profile/transactions-sheet/transactions-sheet.component.ts
import { Component, EventEmitter, Output, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavController, IonModal } from '@ionic/angular';
import { 
  IonHeader, 
  IonToolbar, 
  IonTitle, 
  IonButtons, 
  IonButton, 
  IonIcon, 
  IonContent,
  IonSkeletonText 
} from '@ionic/angular/standalone';
import { TransactionService, FormattedTransaction } from 'src/app/services/transaction.service';

@Component({
  selector: 'app-transactions-sheet',
  templateUrl: './transactions-sheet.component.html',
  styleUrls: ['./transactions-sheet.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonButton,
    IonIcon,
    IonContent,
    IonSkeletonText
  ]
})
export class TransactionsSheetComponent implements OnInit {
  @Output() onClose = new EventEmitter<void>();
  @ViewChild(IonModal) modal!: IonModal;

  transactions: FormattedTransaction[] = [];
  isLoading = false;
  hasMore = true;
  limit = 20;
  offset = 0;
  total = 0;
  imageErrors: { [key: string]: boolean } = {};

  constructor(
    private _transactionService: TransactionService,
    private _nav: NavController
  ) {}

  ngOnInit() {
    this.loadTransactions();
  }

  async loadTransactions(loadMore: boolean = false) {
    if (this.isLoading) return;

    this.isLoading = true;

    try {
      if (!loadMore) {
        this.offset = 0;
        this.transactions = [];
        this.hasMore = true;
      }

      const response = await this._transactionService.getTransactions(this.limit, this.offset).toPromise();
      
      if (response) {
        const formatted = response.transactions.map(tx => 
          this._transactionService.formatTransaction(tx)
        );

        if (loadMore) {
          this.transactions = [...this.transactions, ...formatted];
        } else {
          this.transactions = formatted;
        }

        this.total = response.total;
        this.offset += this.limit;
        this.hasMore = this.transactions.length < this.total;
      }
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      this.isLoading = false;
    }
  }

  close() {
    this.onClose.emit();
  }

  formatTimestamp(date: Date): string {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));

    if (hours < 1) {
      const minutes = Math.floor(diff / (1000 * 60));
      return `vor ${minutes}m`;
    } else if (hours < 24) {
      return `vor ${hours}h`;
    } else {
      const days = Math.floor(hours / 24);
      if (days < 7) {
        return `vor ${days}d`;
      } else if (days < 30) {
        const weeks = Math.floor(days / 7);
        return `vor ${weeks}w`;
      } else if (days < 365) {
        const months = Math.floor(days / 30);
        return `vor ${months}mo`;
      } else {
        return date.toLocaleDateString('de-DE');
      }
    }
  }

  viewInvoice(transaction: FormattedTransaction) {
    if (transaction.invoice?.downloadUrl) {
      window.open(transaction.invoice.downloadUrl, '_blank');
    }
  }

  viewBusiness(transaction: FormattedTransaction) {
    if (transaction.businessHubId) {
      this._nav.navigateForward(['/dashboard/business-hub', transaction.businessHubId]);
      this.close();
    }
  }

  loadMore() {
    if (!this.isLoading && this.hasMore) {
      this.loadTransactions(true);
    }
  }

  onImageError(event: Event, transactionId: string) {
    this.imageErrors[transactionId] = true;
  }

  getCategoryLabel(category: string): string {
    const labels: { [key: string]: string } = {
      'order': 'Bestellung',
      'invoice': 'Rechnung',
      'system': 'System',
      'admin': 'Admin'
    };
    return labels[category] || category;
  }
}